import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import toast from 'react-hot-toast';
import { api } from '../../api/api';

const STATUS_MAP = {
  new: { label: '새 문의', color: 'error' },
  read: { label: '확인', color: 'info' },
  replied: { label: '답변 완료', color: 'success' },
  closed: { label: '종료', color: 'default' },
};

export default function ContactSettings() {
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  const [inquiries, setInquiries] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchEmail();
    fetchInquiries(1);
  }, []);

  const fetchEmail = async () => {
    try {
      const { data } = await api.get('/config/contact-email');
      setEmail(data.email);
      setOriginalEmail(data.email);
    } catch (_) {} finally { setLoadingEmail(false); }
  };

  const saveEmail = async () => {
    setSaving(true);
    try {
      await api.put('/config/contact-email', { email });
      toast.success('수신 이메일이 변경되었습니다.');
      setOriginalEmail(email);
    } catch (e) { toast.error('저장 실패'); }
    finally { setSaving(false); }
  };

  const fetchInquiries = async (p) => {
    setLoadingList(true);
    try {
      const { data } = await api.get(`/contact?page=${p}&limit=15`);
      setInquiries(data.inquiries);
      setTotalPages(data.totalPages);
      setPage(data.page);
    } catch (_) {} finally { setLoadingList(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/contact/${id}/status`, { status });
      toast.success('상태가 변경되었습니다.');
      fetchInquiries(page);
    } catch (_) { toast.error('변경 실패'); }
  };

  return (
    <Box>
      {/* 수신 이메일 설정 */}
      <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>Contact Recipient Email</Typography>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Typography sx={{ fontSize: 13, color: '#888', mb: 1 }}>
          고객 문의 폼에서 접수된 내용이 이 이메일로 발송됩니다.
        </Typography>
        {loadingEmail ? <CircularProgress size={20} /> : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField size="small" value={email} onChange={(e) => setEmail(e.target.value)}
              sx={{ width: 320, '& .MuiInputBase-root': { fontSize: 13 } }} />
            <Button variant="contained" size="small" startIcon={<SaveIcon />}
              onClick={saveEmail} disabled={saving || email === originalEmail}>
              저장
            </Button>
          </Box>
        )}
      </Paper>

      {/* 문의 목록 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Customer Inquiries</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => fetchInquiries(page)}>새로고침</Button>
      </Box>

      {loadingList ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 12, color: '#888' } }}>
                  <TableCell>상태</TableCell>
                  <TableCell>이름</TableCell>
                  <TableCell>이메일</TableCell>
                  <TableCell>제목</TableCell>
                  <TableCell>접수일</TableCell>
                  <TableCell>변경</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inquiries.map((inq) => (
                  <TableRow key={inq._id} hover>
                    <TableCell>
                      <Chip label={STATUS_MAP[inq.status]?.label || inq.status}
                        color={STATUS_MAP[inq.status]?.color || 'default'}
                        size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{inq.name}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: '#555' }}>{inq.email}</TableCell>
                    <TableCell sx={{ fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inq.subject}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: '#888' }}>
                      {new Date(inq.createdAt).toLocaleString('ko-KR')}
                    </TableCell>
                    <TableCell>
                      <Select size="small" value={inq.status} onChange={(e) => updateStatus(inq._id, e.target.value)}
                        sx={{ fontSize: 12, height: 28 }}>
                        <MenuItem value="new" sx={{ fontSize: 12 }}>새 문의</MenuItem>
                        <MenuItem value="read" sx={{ fontSize: 12 }}>확인</MenuItem>
                        <MenuItem value="replied" sx={{ fontSize: 12 }}>답변 완료</MenuItem>
                        <MenuItem value="closed" sx={{ fontSize: 12 }}>종료</MenuItem>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
                {inquiries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#888' }}>
                      접수된 문의가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
              <Button size="small" disabled={page <= 1} onClick={() => fetchInquiries(page - 1)}>이전</Button>
              <Typography sx={{ fontSize: 13, lineHeight: '30px' }}>{page} / {totalPages}</Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => fetchInquiries(page + 1)}>다음</Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
